import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { Pill, Select } from "@libertify/ui";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import type { AnalyticsVersion } from "types/";
import { ALL_VERSIONS, PREVIEW_VERSION } from "./constants/constants";

dayjs.extend(localizedFormat);

const T_PREFIX = "media.projects.analytics.versions";

export type AnalyticsVersionSelectProps = {
  /** The versions viewers saw, the latest first. */
  versions: AnalyticsVersion[];
  /** The picked version, undefined until known. */
  value: string | undefined;
  onChange: (version: string) => void;
  loading?: boolean;
  error?: boolean;
};

export const AnalyticsVersionSelect = ({
  versions,
  value,
  onChange,
  loading = false,
  error = false,
}: AnalyticsVersionSelectProps) => {
  const t = useTranslate();
  const empty = !loading && !error && versions.length === 0;

  const versionLabel = (version: AnalyticsVersion) => (
    <Flex align="center" gap={8}>
      <span>
        {version.project_publication_id === PREVIEW_VERSION
          ? t(`${T_PREFIX}.preview`)
          : t(`${T_PREFIX}.versionOf`, {
              date: dayjs(version.first_seen_at).format("ll"),
            })}
      </span>
      <Typography.Text type="secondary">
        {t(`${T_PREFIX}.visits`, { count: version.sessions })}
      </Typography.Text>
      {version.is_published ? (
        <Pill tone="success">{t(`${T_PREFIX}.live`)}</Pill>
      ) : null}
    </Flex>
  );

  // Publications first, then what viewers saw outside one, then everything.
  const publications = versions.filter(
    (v) => v.project_publication_id !== PREVIEW_VERSION
  );
  const preview = versions.filter(
    (v) => v.project_publication_id === PREVIEW_VERSION
  );
  const options = [
    ...[...publications, ...preview].map((version) => ({
      value: version.project_publication_id,
      label: versionLabel(version),
    })),
    ...(versions.length > 0
      ? [{ value: ALL_VERSIONS, label: t(`${T_PREFIX}.all`) }]
      : []),
  ];

  return (
    <Select
      aria-label={t(`${T_PREFIX}.label`)}
      options={options}
      // Without the list, a picked id has no label and would show raw.
      value={empty || error ? undefined : value}
      onChange={(next: string) => onChange(next)}
      loading={loading}
      disabled={loading || error || empty}
      placeholder={empty ? t(`${T_PREFIX}.empty`) : undefined}
      status={error ? "error" : "default"}
      helpText={error ? t(`${T_PREFIX}.loadError`) : undefined}
      popupMatchSelectWidth={false}
    />
  );
};
